import { useRef, useEffect } from 'react';

import { ExtraSectionIdentifiers } from '../activity-builder.apollo';
import { useFeatureResumeContentFromSection } from 'approot/feature-flags/use-feature-resume-content-from-section';
import { useSetRecentlyViewedContent } from 'approot/home/recently-viewed-content/use-set-recently-viewed-content';
import type { SetRecentlyViewedContentInput } from '../../../gql/schema-types';

type Props = {
  activeSectionId: string;
  classId: number | null | undefined;
  contentId: number;
  contentType: SetRecentlyViewedContentInput['contentType'];
  unitId: number | undefined;
};

/**
 * Debounces and persists the last-viewed section for a lesson or assessment
 *
 * Fires a `setRecentlyViewedContent` mutation 400ms after `activeSectionId`
 * changes, so navigating quickly through slides doesn't flood the server.
 * No-ops when the feature flag is off or when a class/content/unit context is
 * absent. Sends `lastViewedSectionId: null` when on the introduction or
 * conclusion slide to clear any previously saved position.
 */
export const useTrackLastViewedSection = ({
  activeSectionId,
  classId,
  contentId,
  contentType,
  unitId,
}: Props) => {
  const featureResumeLesson = useFeatureResumeContentFromSection();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [setRecentlyViewedContent] = useSetRecentlyViewedContent();

  useEffect(() => {
    if (
      !featureResumeLesson ||
      !classId ||
      !contentId ||
      !activeSectionId ||
      !unitId
    ) {
      return;
    }

    const isIntroOrConclusion =
      activeSectionId === ExtraSectionIdentifiers.Introduction ||
      activeSectionId === ExtraSectionIdentifiers.Conclusion;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setRecentlyViewedContent({
        variables: {
          input: {
            classId,
            contentId,
            contentType,
            visitedFromUnitId: unitId,
            lastViewedSectionId: isIntroOrConclusion ? null : activeSectionId,
          },
        },
      }).catch(() => {});
    }, 400);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionId, featureResumeLesson, unitId]);
};
